import { normalizeEmail } from "../../server/rules/inbox";
import { el } from "./dom";

// The To of one email to several people (brief 1E-15, mockup v65 .tochips): addresses picked from
// the record's contacts or typed, a comma or Enter between, each shown as a chip with a remove
// button. Each address is checked as it's added; the server checks them all again.

export interface EmailChips {
  el: HTMLElement;
  /** The addresses on chips, and whatever is still typed in the box (sent as one more). */
  values(): string[];
}

export function emailChips(o: {
  id: string;
  label: string;
  start: readonly string[];
  /** The record's contacts: the address, and "Name · address" to pick from. */
  options: readonly { value: string; label: string }[];
  /** Called after each change, so a form can save or clear its notice. */
  changed?: () => void;
}): EmailChips {
  const to: string[] = [];
  const box = el("div", { class: "tochips" });
  const input = el("input", {
    id: o.id,
    type: "text",
    list: `${o.id}-list`,
    autocomplete: "off",
    "aria-describedby": `${o.id}-said`,
  });
  const said = el("span", { class: "hint", id: `${o.id}-said`, role: "status" });
  const list = el(
    "datalist",
    { id: `${o.id}-list` },
    ...o.options.map((p) => el("option", { value: p.value }, p.label)),
  );

  // The box stays where it is (chips go in front of it), so typing and focus carry on.
  function draw() {
    input.placeholder = to.length ? "Add another" : "Type or pick, comma between";
    for (const c of [...box.querySelectorAll(":scope > .chip")]) c.remove();
    input.before(
      ...to.map((t, i) => {
        const x = el("button", { type: "button", class: "tox", "aria-label": `Remove ${t}` }, "×");
        x.addEventListener("click", () => {
          to.splice(i, 1);
          draw();
          o.changed?.();
          input.focus();
        });
        return el("span", { class: "chip info" }, `${t} `, x);
      }),
    );
  }

  /** Adds what's typed (one or more, comma between); answers false when one isn't an address. */
  function add(raw: string): boolean {
    const parts = raw
      .split(/[,;]/)
      .map((p) => p.trim())
      .filter(Boolean);
    const bad: string[] = [];
    for (const p of parts) {
      const e = normalizeEmail(/<([^>]+)>/.exec(p)?.[1] ?? p);
      if (!e) bad.push(p);
      else if (!to.includes(e)) to.push(e);
    }
    input.value = bad.join(", ");
    said.textContent = bad.length ? `Check the email address: ${bad[0]}.` : "";
    draw();
    o.changed?.();
    return !bad.length;
  }

  for (const s of o.start) add(s);
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(input.value);
      input.focus();
    } else if (e.key === "Backspace" && !input.value && to.length) {
      to.pop();
      draw();
      o.changed?.();
    }
  });
  // A comma or semicolon typed (or pasted) ends an address, and so does a pick from the list
  // (an input that isn't typing). Leaving the box keeps what's typed there, so a tap on Send
  // isn't moved by a new chip; it's sent as one more address.
  input.addEventListener("input", (e) => {
    const picked = !(e instanceof InputEvent) || e.inputType === "insertReplacementText";
    if (/[,;]/.test(input.value) || (picked && input.value.trim())) add(input.value);
    else said.textContent = "";
  });
  box.append(input);
  draw();
  return {
    el: el("div", { class: "field tofield" }, el("label", { for: o.id }, o.label), box, list, said),
    values: () => [...to, ...(input.value.trim() ? [input.value.trim()] : [])],
  };
}
